import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Content, Organization } from "@/types";
import { AlertColor } from "@mui/material";

interface Snackbar {
  isOpen: boolean;
  message: string | null;
  severity: AlertColor;
}

interface UIState {
  isSummaryOpen: boolean;
  selectedMenuItem: Content | null;
  isLoading: boolean;
  snackbar: Snackbar;
  loading: {
    isActive: boolean;
    queue: string[];
  };
  activeOrganization: Organization['id'] | null;
  container: "fixed" | "fluid";
}

const initialState: UIState = {
  isSummaryOpen: true,
  selectedMenuItem: null,
  isLoading: false,
  loading: {
    isActive: false,
    queue: [],
  },
  snackbar: {
    isOpen: false,
    message: null,
    severity: "info",
  },
  activeOrganization: null,
  container: "fixed",
};

export const unSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    openSummary: (state) => {
      state.isSummaryOpen = true;
    },
    closeSummary: (state) => {
      state.isSummaryOpen = false;
    },
    openItem: (state, action: PayloadAction<Content>) => {
      state.selectedMenuItem = action.payload;
    },
    closeItem: (state) => {
      state.selectedMenuItem = null;
    },
    openSnackbar: (state, action: PayloadAction<Omit<Snackbar, "isOpen">>) => {
      state.snackbar = { ...action.payload, isOpen: true };
    },
    closeSnackbar: (state) => {
      state.snackbar.isOpen = false;
    },
    addLoadingTask: (state, action: PayloadAction<string>) => {
      state.loading.isActive = true;
      state.loading.queue.push(action.payload);
    },
    deleteLoadingTask: (state, action: PayloadAction<string>) => {
      const task = action.payload;
      const queue = state.loading.queue;

      if (queue.length && queue.includes(task)) {
        const newQueue = queue.filter((t) => t !== task);
        state.loading.queue = newQueue;

        if (!newQueue.length) {
          state.loading.isActive = false;
        }
      }
    },
    setActiveOrganization: (state, action: PayloadAction<Organization['id']>) => {
      state.activeOrganization = action.payload;
    },
    setContainer: (state, action: PayloadAction<"fixed" | "fluid">) => {
      state.container = action.payload;
    },
    setSelectedMenuItem: (state, action: PayloadAction<Content | null>) => {
      state.selectedMenuItem = action.payload;
    }
  },
});

export const {
  openSummary,
  closeSummary,
  openItem,
  closeItem,
  openSnackbar,
  closeSnackbar,
  addLoadingTask,
  deleteLoadingTask,
  setActiveOrganization,
  setContainer,
  setSelectedMenuItem,
} = unSlice.actions;

export default unSlice.reducer;
